import React, { useState, useEffect } from 'react';
import { Play, Copy, Check, RotateCcw, Image as ImageIcon, X, FileText, ExternalLink, Download } from 'lucide-react';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Textarea } from './ui/textarea';
import { codeExamples } from '../mock';
import axios from 'axios';
import { usePlayground } from '../contexts/PlaygroundContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PLAYGROUND_CODE_KEY = 'blox_playground_code';

const CodePlayground = () => {
  const [selectedExample, setSelectedExample] = useState('hello-world');
  const [code, setCode] = useState(() => {
    // Verificar localStorage al inicializar el estado
    const savedCode = localStorage.getItem(PLAYGROUND_CODE_KEY);
    if (savedCode) {
      return savedCode;
    }
    return codeExamples['hello-world'];
  });
  const [output, setOutput] = useState('');
  const [images, setImages] = useState([]);
  const [htmlFiles, setHtmlFiles] = useState([]);
  const [activeHtmlIdx, setActiveHtmlIdx] = useState(0);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expandedImage, setExpandedImage] = useState(null);
  const [isCustomCode, setIsCustomCode] = useState(() => {
    // Verificar si hay código guardado al inicializar
    return !!localStorage.getItem(PLAYGROUND_CODE_KEY);
  });
  
  const { playgroundCode, clearPlaygroundCode } = usePlayground();

  // Efecto para limpiar localStorage después de cargarlo
  useEffect(() => {
    const savedCode = localStorage.getItem(PLAYGROUND_CODE_KEY);
    if (savedCode) {
      localStorage.removeItem(PLAYGROUND_CODE_KEY);
      clearPlaygroundCode();
    }
    // Solo al montar: clearPlaygroundCode es estable (viene del contexto).
  }, []); // Solo al montar

  // Efecto para cargar código desde el contexto (navegación dentro de la SPA)
  useEffect(() => {
    if (playgroundCode) {
      setCode(playgroundCode);
      setIsCustomCode(true);
      setSelectedExample('hello-world');
      setOutput('');
      setImages([]);
      clearPlaygroundCode();
    }
  }, [playgroundCode, clearPlaygroundCode]);

  const examples = [
    { id: 'hello-world', label: 'Hola Mundo' },
    { id: 'functions', label: 'Funciones' },
    { id: 'loops', label: 'Bucles' },
    { id: 'conditionals', label: 'Condicionales' }
  ];

  const handleExampleChange = (exampleId) => {
    setSelectedExample(exampleId);
    setCode(codeExamples[exampleId]);
    setOutput('');
    setImages([]);
    setIsCustomCode(false); // Ya no es código personalizado
  };

  const handleRun = async () => {
    setLoading(true);
    setOutput('Ejecutando código Blox...');
    setImages([]);
    setHtmlFiles([]);
    setActiveHtmlIdx(0);
    
    try {
      const response = await axios.post(
        `${API}/execute-blox`,
        { 
          code: code,
          timeout: 5
        }
      );
      
      if (response.data.success) {
        setOutput(response.data.output);
        // Procesar imágenes si existen
        if (response.data.images && response.data.images.length > 0) {
          setImages(response.data.images);
        }
        // Procesar archivos HTML si existen (IMPRIMIR_MD, etc.)
        if (response.data.html_files && response.data.html_files.length > 0) {
          setHtmlFiles(response.data.html_files);
        }
      } else {
        // Mostrar mensaje de error detallado del servidor
        const errorOutput = response.data.output || '';
        const errorMsg = response.data.error || 'Error desconocido';
        
        if (errorOutput) {
          // Si hay salida con el error, mostrarla completa
          setOutput(`${errorOutput}`);
        } else {
          setOutput(`❌ Error:\n${errorMsg}`);
        }
      }
    } catch (error) {
      setOutput(`❌ Error de conexión:\n${error.message}\n\nAsegúrate de que el servidor Blox esté corriendo.`);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleReset = () => {
    setCode(codeExamples[selectedExample]);
    setOutput('');
    setImages([]);
    setIsCustomCode(false);
  };

  return (
    <section id="playground" className="py-24 bg-slate-950">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Prueba <span className="bg-gradient-to-r from-cyan-400 to-teal-400 text-transparent bg-clip-text">Blox</span> en tu navegador
          </h2>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Escribe y ejecuta código Blox en tiempo real. ¡Experimenta y aprende!
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <Tabs value={selectedExample} onValueChange={handleExampleChange} className="w-full">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 bg-slate-900 border border-slate-800 p-1">
              {examples.map((example) => (
                <TabsTrigger
                  key={example.id}
                  value={example.id}
                  className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500 data-[state=active]:to-teal-500 data-[state=active]:text-slate-950 text-slate-300"
                >
                  {example.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {examples.map((example) => (
              <TabsContent key={example.id} value={example.id} className="mt-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Editor */}
                  <div className="bg-slate-900/80 backdrop-blur-sm rounded-2xl border border-slate-800 overflow-hidden shadow-2xl">
                    <div className="flex items-center justify-between px-4 py-3 bg-slate-800/50 border-b border-slate-700">
                      <div className="flex items-center gap-2">
                        <div className="flex gap-2">
                          <div className="w-3 h-3 rounded-full bg-red-500"></div>
                          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                          <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        </div>
                        <span className="text-xs text-slate-400 ml-2">
                          {isCustomCode ? 'código desde docs' : 'editor.blox'}
                        </span>
                        {isCustomCode && (
                          <span className="text-xs bg-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded-full">
                            De Documentación
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-slate-400 hover:text-white h-8"
                          onClick={handleReset}
                          title="Restaurar ejemplo"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-slate-400 hover:text-white h-8"
                          onClick={handleCopy}
                          title="Copiar código"
                        >
                          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                    <div className="p-4">
                      <Textarea
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        className="w-full min-h-[300px] bg-slate-950 border-slate-700 text-slate-300 font-mono text-sm leading-relaxed resize-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                        placeholder="Escribe tu código Blox aquí..."
                        spellCheck={false}
                      />
                    </div>
                    <div className="px-4 pb-4">
                      <Button
                        className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-slate-950 font-semibold"
                        onClick={handleRun}
                        disabled={loading}
                      >
                        <Play className="w-4 h-4 mr-2" />
                        {loading ? 'Ejecutando...' : 'Ejecutar Código'}
                      </Button>
                    </div>
                  </div>

                  {/* Output */}
                  <div className="bg-slate-900/80 backdrop-blur-sm rounded-2xl border border-slate-800 overflow-hidden shadow-2xl">
                    <div className="flex items-center gap-2 px-4 py-3 bg-slate-800/50 border-b border-slate-700">
                      <div className="flex gap-2">
                        <div className="w-3 h-3 rounded-full bg-slate-600"></div>
                        <div className="w-3 h-3 rounded-full bg-slate-600"></div>
                        <div className="w-3 h-3 rounded-full bg-slate-600"></div>
                      </div>
                      <span className="text-xs text-slate-400 ml-2">salida</span>
                      {images.length > 0 && (
                        <span className="ml-auto text-xs text-cyan-400 flex items-center gap-1">
                          <ImageIcon className="w-3 h-3" />
                          {images.length} gráfico{images.length > 1 ? 's' : ''}
                        </span>
                      )}
                      {htmlFiles.length > 0 && (
                        <span
                          className={`${images.length > 0 ? 'ml-3' : 'ml-auto'} text-xs text-amber-400 flex items-center gap-1`}
                          data-testid="playground-html-counter"
                        >
                          <FileText className="w-3 h-3" />
                          {htmlFiles.length} HTML
                        </span>
                      )}
                    </div>
                    <div className="p-6 min-h-[300px] max-h-[500px] overflow-y-auto">
                      {output ? (
                        <>
                          <pre className="text-cyan-400 font-mono text-sm leading-relaxed whitespace-pre-wrap">
                            {output}
                          </pre>
                          
                          {/* Sección de gráficos */}
                          {images.length > 0 && (
                            <div className="mt-6 pt-6 border-t border-slate-700">
                              <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                                <ImageIcon className="w-4 h-4 text-cyan-400" />
                                Gráficos Generados
                              </h4>
                              <div className="grid grid-cols-1 gap-4">
                                {images.map((img, index) => (
                                  <div 
                                    key={index} 
                                    className="bg-slate-950 rounded-lg border border-slate-700 overflow-hidden"
                                  >
                                    <div className="px-3 py-2 bg-slate-800/50 border-b border-slate-700 flex items-center justify-between">
                                      <span className="text-xs text-slate-400 font-mono">
                                        {img.nombre || `grafico_${index + 1}.png`}
                                      </span>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-slate-400 hover:text-white h-6 text-xs"
                                        onClick={() => setExpandedImage(img)}
                                      >
                                        Ampliar
                                      </Button>
                                    </div>
                                    <div className="p-4 flex justify-center bg-white/5">
                                      <img 
                                        src={`data:image/png;base64,${img.data}`}
                                        alt={img.nombre || `Gráfico ${index + 1}`}
                                        className="max-w-full h-auto rounded cursor-pointer hover:opacity-90 transition-opacity"
                                        onClick={() => setExpandedImage(img)}
                                        data-testid={`playground-image-${index}`}
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Sección de HTML (IMPRIMIR_MD u otros) */}
                          {htmlFiles.length > 0 && (
                            <div
                              className="mt-6 pt-6 border-t border-slate-700"
                              data-testid="playground-html-section"
                            >
                              <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                                <FileText className="w-4 h-4 text-amber-400" />
                                Documento{htmlFiles.length > 1 ? 's' : ''} HTML
                              </h4>

                              {/* Tabs si hay varios */}
                              {htmlFiles.length > 1 && (
                                <div className="flex flex-wrap gap-2 mb-3">
                                  {htmlFiles.map((h, i) => (
                                    <button
                                      key={i}
                                      onClick={() => setActiveHtmlIdx(i)}
                                      className={`px-3 py-1 rounded-md text-xs font-mono transition-colors ${
                                        activeHtmlIdx === i
                                          ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                                          : 'bg-slate-800 text-slate-400 border border-slate-700 hover:text-white'
                                      }`}
                                      data-testid={`playground-html-tab-${i}`}
                                    >
                                      {h.nombre}
                                    </button>
                                  ))}
                                </div>
                              )}

                              {(() => {
                                const h = htmlFiles[activeHtmlIdx] || htmlFiles[0];
                                if (!h) return null;
                                // Decodificar base64 → HTML string (compatible con Safari)
                                let htmlString = '';
                                try {
                                  const binary = atob(h.data);
                                  // Convertir binario a UTF-8 correctamente
                                  const bytes = new Uint8Array(binary.length);
                                  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
                                  htmlString = new TextDecoder('utf-8').decode(bytes);
                                } catch (err) {
                                  htmlString = '<p style="color:#a00">Error decodificando HTML.</p>';
                                }
                                // URL para los botones (descargar / nueva pestaña): usa Blob (Safari friendly)
                                const blob = new Blob([htmlString], { type: 'text/html;charset=utf-8' });
                                const blobUrl = URL.createObjectURL(blob);
                                return (
                                  <div className="bg-slate-950 rounded-lg border border-slate-700 overflow-hidden">
                                    <div className="px-3 py-2 bg-slate-800/50 border-b border-slate-700 flex items-center justify-between gap-2">
                                      <span className="text-xs text-slate-400 font-mono truncate">
                                        {h.nombre} ({(h.size / 1024).toFixed(1)} KB)
                                      </span>
                                      <div className="flex gap-2">
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="text-slate-400 hover:text-white h-6 text-xs gap-1"
                                          onClick={() => window.open(blobUrl, '_blank', 'noopener,noreferrer')}
                                          data-testid="playground-html-open-new-tab"
                                        >
                                          <ExternalLink className="w-3 h-3" />
                                          Nueva pestaña
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="text-slate-400 hover:text-white h-6 text-xs gap-1"
                                          onClick={() => {
                                            const link = document.createElement('a');
                                            link.href = blobUrl;
                                            link.download = h.nombre || 'salida.html';
                                            link.click();
                                          }}
                                          data-testid="playground-html-download"
                                        >
                                          <Download className="w-3 h-3" />
                                          Descargar
                                        </Button>
                                      </div>
                                    </div>
                                    <iframe
                                      title={h.nombre}
                                      srcDoc={htmlString}
                                      sandbox="allow-scripts allow-popups"
                                      className="w-full bg-white"
                                      style={{ height: '500px', border: 'none' }}
                                      data-testid="playground-html-iframe"
                                    />
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="flex flex-col items-center justify-center h-full text-slate-500">
                          <p className="text-center mb-4">
                            Escribe tu código y haz clic en &quot;Ejecutar Código&quot; para ver el resultado
                          </p>
                          <p className="text-xs text-center text-slate-600">
                            💡 Puedes editar los ejemplos o escribir tu propio código
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>

        {/* Tips */}
        <div className="max-w-5xl mx-auto mt-8">
          <div className="bg-gradient-to-r from-cyan-500/10 to-teal-500/10 border border-cyan-500/20 rounded-xl p-6">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <span className="text-cyan-400">💡</span> Tips para el Editor
            </h3>
            <ul className="text-slate-400 text-sm space-y-2">
              <li>• Puedes <strong>editar</strong> cualquier ejemplo o escribir tu propio código desde cero</li>
              <li>• Usa el botón <RotateCcw className="w-3 h-3 inline" /> para restaurar el ejemplo original</li>
              <li>• El código se ejecuta en <strong>tiempo real</strong> usando tu intérprete BLOX.EXE</li>
              <li>• Si hay errores de sintaxis, aparecerán en la salida</li>
              <li>• Los <strong>gráficos PNG</strong> generados por tu código se mostrarán automáticamente</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Modal para imagen ampliada */}
      {expandedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setExpandedImage(null)}
          data-testid="image-modal"
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full">
            <Button
              size="sm"
              variant="ghost"
              className="absolute -top-10 right-0 text-white hover:text-cyan-400"
              onClick={() => setExpandedImage(null)}
            >
              <X className="w-5 h-5 mr-1" />
              Cerrar
            </Button>
            <div className="bg-slate-900 rounded-lg overflow-hidden border border-slate-700">
              <div className="px-4 py-2 bg-slate-800 border-b border-slate-700">
                <span className="text-sm text-slate-300 font-mono">
                  {expandedImage.nombre || 'grafico.png'}
                </span>
              </div>
              <div className="p-4 bg-white/5 flex justify-center">
                <img 
                  src={`data:image/png;base64,${expandedImage.data}`}
                  alt={expandedImage.nombre || 'Gráfico ampliado'}
                  className="max-w-full max-h-[70vh] object-contain"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default CodePlayground;